import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { KeyRound, Percent, Info } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminSettings() {
    const [s, setS] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = async () => { const { data } = await api.get('/admin/settings'); setS(data); };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                stripe_mode: s.stripe_mode,
                commission_pct: Number(s.commission_pct),
                currency: s.currency,
                platform_name: s.platform_name,
                support_email: s.support_email,
                smtp_host: s.smtp_host || null,
                smtp_port: s.smtp_port ? Number(s.smtp_port) : null,
                smtp_user: s.smtp_user || null,
                smtp_from: s.smtp_from || null,
                smtp_use_tls: s.smtp_use_tls,
            };
            if (s._newSecret) payload.stripe_secret_key = s._newSecret;
            if (s._newPub !== undefined) payload.stripe_publishable_key = s._newPub;
            if (s._newWebhook !== undefined) payload.stripe_webhook_secret = s._newWebhook;
            if (s._newSmtpPassword) payload.smtp_password = s._newSmtpPassword;
            await api.put('/admin/settings', payload);
            toast.success('Ajustes guardados');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
        finally { setSaving(false); }
    };

    if (!s) return <div className="text-sm text-muted-foreground">Cargando…</div>;

    return (
        <div className="space-y-6 max-w-3xl">
            <div>
                <h1 className="text-2xl font-semibold tracking-tight">Ajustes</h1>
                <p className="text-sm text-muted-foreground">Configura comisiones y llaves de Stripe.</p>
            </div>

            <div className="rounded-xl border bg-card p-5 space-y-4">
                <div className="flex items-center gap-2"><Percent className="h-4 w-4" /> <h2 className="font-medium">Comisiones</h2></div>
                <div className="grid grid-cols-2 gap-3">
                    <div><Label>Comisión comprador (%)</Label><Input type="number" min="0" max="50" step="0.5" value={s.commission_pct || 0} onChange={(e) => setS({ ...s, commission_pct: e.target.value })} data-testid="settings-commission" /></div>
                    <div><Label>Moneda por defecto</Label><Input value={s.currency || 'MXN'} onChange={(e) => setS({ ...s, currency: e.target.value.toUpperCase() })} /></div>
                </div>
            </div>

            <div className="rounded-xl border bg-card p-5 space-y-4">
                <div className="flex items-center gap-2"><KeyRound className="h-4 w-4" /><h2 className="font-medium">Llaves de Stripe</h2></div>
                <Alert>
                    <Info className="h-4 w-4" />
                    <AlertTitle>Confidencial</AlertTitle>
                    <AlertDescription>Estas llaves se guardan cifradas en base de datos. Deja el campo vacío para mantener la actual.</AlertDescription>
                </Alert>
                <div>
                    <Label>Modo</Label>
                    <Select value={s.stripe_mode || 'test'} onValueChange={(v) => setS({ ...s, stripe_mode: v })}>
                        <SelectTrigger data-testid="settings-stripe-mode"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="test">Test</SelectItem><SelectItem value="live">Live</SelectItem></SelectContent>
                    </Select>
                </div>
                <div><Label>Stripe Secret Key (sk_...)</Label>
                    <Input type="password" placeholder={s.stripe_secret_key_masked || 'Sin configurar'} onChange={(e) => setS({ ...s, _newSecret: e.target.value })} data-testid="settings-stripe-secret" />
                    <p className="text-xs text-muted-foreground mt-1">Actual: <span className="font-mono">{s.stripe_secret_key_masked || '—'}</span></p>
                </div>
                <div><Label>Stripe Publishable Key (pk_...)</Label>
                    <Input placeholder="pk_test_..." defaultValue={s.stripe_publishable_key || ''} onChange={(e) => setS({ ...s, _newPub: e.target.value })} data-testid="settings-stripe-pub" />
                </div>
                <div><Label>Stripe Webhook Secret (whsec_...)</Label>
                    <Input type="password" placeholder={s.stripe_webhook_secret_masked || 'Sin configurar'} onChange={(e) => setS({ ...s, _newWebhook: e.target.value })} data-testid="settings-stripe-webhook" />
                </div>
            </div>

            <div className="rounded-xl border bg-card p-5 space-y-4">
                <h2 className="font-medium">Plataforma</h2>
                <div className="grid grid-cols-2 gap-3">
                    <div><Label>Nombre</Label><Input value={s.platform_name || ''} onChange={(e) => setS({ ...s, platform_name: e.target.value })} /></div>
                    <div><Label>Email de soporte</Label><Input value={s.support_email || ''} onChange={(e) => setS({ ...s, support_email: e.target.value })} /></div>
                </div>
            </div>

            <div className="rounded-xl border bg-card p-5 space-y-4">
                <h2 className="font-medium">SMTP (correos transaccionales)</h2>
                <p className="text-xs text-muted-foreground">Si no configuras SMTP, los correos se registrarán en logs (modo mock).</p>
                <div className="grid grid-cols-2 gap-3">
                    <div><Label>Host SMTP</Label><Input placeholder="smtp.example.com" value={s.smtp_host || ''} onChange={(e) => setS({ ...s, smtp_host: e.target.value })} data-testid="smtp-host" /></div>
                    <div><Label>Puerto</Label><Input type="number" value={s.smtp_port || 587} onChange={(e) => setS({ ...s, smtp_port: e.target.value })} /></div>
                    <div><Label>Usuario</Label><Input value={s.smtp_user || ''} onChange={(e) => setS({ ...s, smtp_user: e.target.value })} /></div>
                    <div><Label>Contraseña SMTP</Label><Input type="password" placeholder={s.smtp_password_masked || 'Sin configurar'} onChange={(e) => setS({ ...s, _newSmtpPassword: e.target.value })} /></div>
                    <div className="col-span-2"><Label>Remitente (From)</Label><Input placeholder="noreply@okgo.mx" value={s.smtp_from || ''} onChange={(e) => setS({ ...s, smtp_from: e.target.value })} /></div>
                </div>
            </div>

            <div className="flex justify-end">
                <Button onClick={save} disabled={saving} data-testid="settings-save">{saving ? 'Guardando…' : 'Guardar cambios'}</Button>
            </div>

            <IntegrationsSettings />
        </div>
    );
}


function IntegrationsSettings() {
    const [is, setIs] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    const load = async () => {
        const { data } = await api.get('/admin/integrations/settings');
        setIs(data);
        setForm({ envia_test_mode: !!data.envia_test_mode });
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const body = { envia_test_mode: !!form.envia_test_mode };
            ['unsplash_access_key', 'pexels_api_key', 'google_maps_api_key', 'envia_api_key'].forEach(k => {
                if (form[k]) body[k] = form[k];
            });
            await api.put('/admin/integrations/settings', body);
            toast.success('Integraciones guardadas');
            setForm({ envia_test_mode: !!form.envia_test_mode });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
        finally { setSaving(false); }
    };

    if (!is) return null;
    const KeyField = ({ label, hint, name, has }) => (
        <div>
            <Label>{label} {has && <span className="text-emerald-600 text-xs">(configurada ✔)</span>}</Label>
            <Input type="password" placeholder={has ? 'Dejar vacío para conservar' : 'Pega la key aquí'} value={form[name] || ''} onChange={(e) => setForm({ ...form, [name]: e.target.value })} data-testid={`int-${name}`} />
            {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
        </div>
    );

    return (
        <div className="rounded-xl border bg-card p-5 space-y-4" data-testid="integrations-settings">
            <div className="flex items-center justify-between">
                <h2 className="font-medium flex items-center gap-2"><KeyRound className="h-4 w-4 text-red-600" />Integraciones externas</h2>
            </div>
            <p className="text-xs text-muted-foreground">Todas las API keys se cifran antes de guardarse. Reinicia el navegador tras guardar Google Maps para que el cambio surta efecto.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <KeyField label="Unsplash Access Key" name="unsplash_access_key" has={is.has_unsplash_key} hint="Regístrate en unsplash.com/developers y usa la Access Key." />
                <KeyField label="Pexels API Key" name="pexels_api_key" has={is.has_pexels_key} hint="Obtén tu key en pexels.com/api." />
                <KeyField label="Google Maps API Key" name="google_maps_api_key" has={is.has_google_maps_key} hint="Habilita: Maps JavaScript API + Places API. Restríngela por HTTP referrer." />
                <KeyField label="envia.com API Key" name="envia_api_key" has={is.has_envia_key} hint="envia.com → tu dashboard → API Keys. Modo de prueba disponible más abajo." />
            </div>
            <div className="flex items-center gap-2 pt-1">
                <input id="envia-test-mode" type="checkbox" checked={!!form.envia_test_mode} onChange={(e) => setForm({ ...form, envia_test_mode: e.target.checked })} />
                <Label htmlFor="envia-test-mode" className="cursor-pointer">envia.com en modo <b>prueba</b></Label>
            </div>
            <div className="flex justify-end">
                <Button onClick={save} disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="save-integrations">{saving ? 'Guardando…' : 'Guardar integraciones'}</Button>
            </div>
        </div>
    );
}
